// Copyright (c) 2023-present VexFlow contributors: https://github.com/vexflow/vexflow/graphs/contributors
// @author Cyril Silverman
// MIT License
import { getBottomY, getInitialOffset, getTopY } from './articulation';
import { Element } from './element';
import { Metrics } from './metrics';
import { Modifier, ModifierPosition } from './modifier';
import { Stem } from './stem';
import { Tables } from './tables';
import { Category } from './typeguard';
import { log } from './util';
// eslint-disable-next-line
function L(...args) {
    if (Ornament.DEBUG)
        log('VexFlow.Ornament', args);
}
/**
 * Ornament implements ornaments as modifiers that can be
 * attached to notes. The complete list of ornaments is available in
 * `tables.ts` under `VexFlow.ornamentCodes`.
 *
 * See `tests/ornament_tests.ts` for usage examples.
 */
export class Ornament extends Modifier {
    /** Ornaments category string. */
    static get CATEGORY() {
        return Category.Ornament;
    }
    static get minPadding() {
        return Metrics.get('NoteHead.minPadding');
    }
    /** Arrange ornaments inside `ModifierContext` */
    static format(ornaments, state) {
        var _a, _b;
        if (!ornaments || ornaments.length === 0)
            return false;
        let width = 0; // width is used by ornaments, which are always centered on the note head
        let rightShift = state.rightShift; // jazz ornaments calculate r/l shift separately
        let leftShift = state.leftShift;
        for (let i = 0; i < ornaments.length; ++i) {
            const ornament = ornaments[i];
            const increment = 2;
            if (ornament.position === ModifierPosition.RIGHT) {
                ornament.xShift += rightShift + 2;
                rightShift += ornament.width + Ornament.minPadding;
            }
            else if (ornament.position === ModifierPosition.LEFT) {
                ornament.xShift -= leftShift + ornament.width + 2;
                leftShift += ornament.width + Ornament.minPadding;
            }
            else if (ornament.position === ModifierPosition.ABOVE) {
                width = Math.max(ornament.getWidth(), width);
                const note = ornament.getNote();
                let curTop = note.getLineNumber(true) + state.topTextLine;
                const stem = note.getStem();
                if (stem && note.getStemDirection() === Stem.UP) {
                    curTop += Math.abs(stem.getHeight()) / Tables.STAVE_LINE_DISTANCE;
                }
                const numLines = (_b = (_a = note.getStave()) === null || _a === void 0 ? void 0 : _a.getNumLines()) !== null && _b !== void 0 ? _b : 0;
                // make sure that the ornaments stay above the staff
                if (curTop < numLines)
                    state.topTextLine += numLines - curTop;
                ornament.setTextLine(state.topTextLine);
                state.topTextLine += increment;
            }
            else {
                width = Math.max(ornament.getWidth(), width);
                ornament.setTextLine(state.textLine);
                state.textLine += increment;
            }
        }
        // Note: 'legit' ornaments don't consider other modifiers when calculating their
        // X position, but jazz ornaments sometimes need to.
        state.leftShift = leftShift + width / 2;
        state.rightShift = rightShift + width / 2;
        return true;
    }
    /**
     * ornamentNoteTransition means the jazz ornament represents an effect from one note to another,
     * these are generally on the top of the staff.
     */
    static get ornamentNoteTransition() {
        return ['flip', 'jazzTurn', 'smear'];
    }
    /**
     * ornamentAttack indicates something that happens in the attach, placed before the note and
     * any accidentals
     */
    static get ornamentAttack() {
        return ['scoop'];
    }
    /**
     * The ornament is aligned based on the note head, but without regard to whether the
     * stem goes up or down.
     */
    static get ornamentAlignWithNoteHead() {
        return ['doit', 'fall', 'fallLong', 'doitLong', 'scoop'];
    }
    /**
     * An ornament that happens on the release of the note, generally placed after the
     * note and overlapping the next beat/measure..
     */
    static get ornamentRelease() {
        return ['doit', 'fall', 'fallLong', 'doitLong', 'jazzTurn', 'smear', 'flip'];
    }
    static get ornamentLeft() {
        return ['scoop'];
    }
    static get ornamentRight() {
        return ['doit', 'fall', 'fallLong', 'doitLong'];
    }
    static get ornamentYShift() {
        return ['fallLong'];
    }
    /** ornamentArticulation goes above/below the note based on space availablity */
    static get ornamentArticulation() {
        return ['bend', 'plungerClosed', 'plungerOpen'];
    }
    /**
     * Create a new ornament of type `type`, which is an entry in
     * `VexFlow.ornamentCodes` in `tables.ts`.
     */
    constructor(type) {
        super();
        // Default position ABOVE
        this.position = ModifierPosition.ABOVE;
        if (Ornament.ornamentRight.indexOf(type) >= 0) {
            this.position = ModifierPosition.RIGHT;
        }
        if (Ornament.ornamentLeft.indexOf(type) >= 0) {
            this.position = ModifierPosition.LEFT;
        }
        this.type = type;
        this.delayed = false;
        this.renderOptions = {
            accidentalLowerPadding: 3,
            accidentalUpperPadding: 3,
        };
        // some jazz ornaments are above or below depending on stem direction.
        this.adjustForStemDirection = false;
        this.ornamentAlignWithNoteHead = Ornament.ornamentAlignWithNoteHead.indexOf(this.type) >= 0;
        // Is this a jazz ornament that goes between this note and the next note.
        if (Ornament.ornamentNoteTransition.indexOf(this.type) >= 0) {
            this.delayed = true;
        }
        this.text = Tables.ornamentCodes(this.type);
    }
    /** Set note attached to ornament. */
    setNote(note) {
        super.setNote(note);
        // articulations above/below the line can be stacked.
        if (Ornament.ornamentArticulation.indexOf(this.type) >= 0) {
            // Unfortunately we don't know the stem direction.  So we base it
            // on the line number, but also allow it to be overridden.
            if (note.getLineNumber() >= 3) {
                this.position = Modifier.Position.ABOVE;
            }
            else {
                this.position = Modifier.Position.BELOW;
            }
        }
        return this;
    }
    /** Set whether the ornament is to be delayed. */
    setDelayed(delayed) {
        this.delayed = delayed;
        return this;
    }
    /** Set the upper accidental for the ornament. */
    setUpperAccidental(accid) {
        this.accidentalUpper = new Element();
        this.accidentalUpper.setText(Tables.accidentalCodes(accid));
        return this;
    }
    /** Set the lower accidental for the ornament. */
    setLowerAccidental(accid) {
        this.accidentalLower = new Element();
        this.accidentalLower.setText(Tables.accidentalCodes(accid));
        return this;
    }
    /** Render ornament in position next to note. */
    draw() {
        const ctx = this.checkContext();
        const note = this.checkAttachedNote();
        this.setRendered();
        const stave = note.checkStave();
        ctx.openGroup('ornament', this.getAttribute('id'));
        // Get initial coordinates for the modifier position
        const start = note.getModifierStartXY(this.position, this.index);
        let glyphX = start.x;
        const staffSpace = stave.getSpacingBetweenLines();
        const initialOffset = getInitialOffset(note, this.position);
        // If the ornament is aligned with the note head, don't consider the stave y
        // but use the 'natural' modifier y
        let glyphY = this.ornamentAlignWithNoteHead ? start.y : 0;
        if (this.position === ModifierPosition.ABOVE) {
            glyphY = getTopY(note, this.textLine) - (this.textLine + initialOffset) * staffSpace;
        }
        if (this.position === ModifierPosition.BELOW) {
            glyphY = getBottomY(note, this.textLine) + (this.textLine + initialOffset + 1.5) * staffSpace;
        }
        // Ajdust x position if ornament is delayed
        if (this.delayed) {
            let delayXShift = 0;
            const startX = note.getTickContext().getX();
            if (this.delayXShift !== undefined) {
                delayXShift = this.delayXShift;
            }
            else {
                const tickables = note.getVoice().getTickables();
                const index = tickables.indexOf(note);
                const nextContext = index + 1 < tickables.length ? tickables[index + 1].checkTickContext() : undefined;
                if (nextContext) {
                    delayXShift += (nextContext.getX() - startX) * 0.5;
                }
                else {
                    delayXShift += (stave.getX() + stave.getWidth() - glyphX) * 0.5;
                }
                this.delayXShift = delayXShift;
            }
            glyphX += delayXShift;
        }
        L('Rendering ornament: ', this.text.charCodeAt(0), glyphX, glyphY);
        if (this.accidentalLower) {
            this.accidentalLower.renderText(ctx, glyphX + this.xShift - this.accidentalLower.getWidth() * 0.5, glyphY + this.yShift - this.accidentalLower.getTextMetrics().actualBoundingBoxDescent);
            glyphY -= this.accidentalLower.getHeight() + this.renderOptions.accidentalLowerPadding;
        }
        // ornament requires yShift
        if (Ornament.ornamentYShift.indexOf(this.type) >= 0) {
            this.yShift += this.getHeight();
        }
        this.x =
            glyphX -
                (this.position === ModifierPosition.ABOVE || this.position === ModifierPosition.BELOW ? this.width * 0.5 : 0);
        this.y = glyphY;
        this.renderText(ctx, 0, 0);
        if (this.accidentalUpper) {
            glyphY -= this.getHeight() + this.renderOptions.accidentalUpperPadding;
            this.accidentalUpper.renderText(ctx, glyphX + this.xShift - this.accidentalUpper.getWidth() * 0.5, glyphY + this.yShift - this.accidentalUpper.getTextMetrics().actualBoundingBoxDescent);
        }
        ctx.closeGroup();
    }
}
/** To enable logging for this class. Set `VexFlow.Ornament.DEBUG` to `true`. */
Ornament.DEBUG = false;
